import PropTypes from 'prop-types';
import { Box, Chip, ListItemButton, Typography, alpha, useTheme } from '@mui/material';
import {
    WA_GREEN,
    WA_LIGHT_GREEN,
    getUsageTypeColor,
    formatUsageType,
    formatTemplateName,
    stripToPlain,
    getTemplateBody,
} from './whatsAppTemplateUtils';

// ----------------------------------------------------------------------
// Renders a single template row in the left-panel list of WhatsAppTemplateSelector.
// Also usable in WhatsappTemplateList admin table for visual consistency.
// ----------------------------------------------------------------------

export default function WhatsAppTemplateListItem({ template, isSelected, onClick }) {
    const theme = useTheme();
    const typeColor = getUsageTypeColor(template.usage_type, theme);
    const bodyPreview = stripToPlain(getTemplateBody(template));

    return (
        <ListItemButton
            selected={isSelected}
            onClick={onClick}
            sx={{
                py: 1.25,
                px: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderLeft: isSelected
                    ? `3px solid ${WA_LIGHT_GREEN}`
                    : '3px solid transparent',
                bgcolor: isSelected
                    ? alpha(WA_LIGHT_GREEN, 0.06)
                    : 'transparent',
                '&:hover': {
                    bgcolor: isSelected
                        ? alpha(WA_LIGHT_GREEN, 0.1)
                        : theme.palette.grey[50],
                },
                transition: 'all 0.15s ease',
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            color: 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                        }}
                    >
                        {formatTemplateName(template.name)}
                    </Typography>
                    <Chip
                        label={formatUsageType(template.usage_type)}
                        size="small"
                        sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            flexShrink: 0,
                            bgcolor: isSelected
                                ? alpha(WA_LIGHT_GREEN, 0.15)
                                : typeColor.bg,
                            color: isSelected
                                ? WA_GREEN
                                : typeColor.color,
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                </Box>
                {bodyPreview && (
                    <Typography
                        sx={{
                            fontSize: '0.72rem',
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.4,
                        }}
                    >
                        {bodyPreview}
                    </Typography>
                )}
            </Box>
        </ListItemButton>
    );
}

WhatsAppTemplateListItem.propTypes = {
    template: PropTypes.object.isRequired,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func,
};

WhatsAppTemplateListItem.defaultProps = {
    isSelected: false,
    onClick: () => {},
};
